import api from './api/base';
import { apiHandler } from '../utils/apiUtils';

/**
 * Unified sharing service for different entity types
 * Supports sandboxes, templates, and AI tutors
 */

// Entity type constants
export const ENTITY_TYPES = {
  SANDBOX: 'sandbox',
  TEMPLATE: 'template',
  AI_TUTOR: 'ai_tutor',
  GROUP: 'group',
  COURSE: 'course',
};

// Map entity types to their API endpoints and parameter names
const ENTITY_CONFIG = {
  [ENTITY_TYPES.SANDBOX]: {
    endpoint: '/share/sandbox-access',
    paramName: 'sandbox',
  },
  [ENTITY_TYPES.TEMPLATE]: {
    endpoint: '/share/template-access',
    paramName: 'template',
  },
  [ENTITY_TYPES.AI_TUTOR]: {
    endpoint: '/share/aitutor-access',
    paramName: 'ai_tutor',
  },
  [ENTITY_TYPES.GROUP]: {
    endpoint: '/share/group-access',
    paramName: 'group',
  },
  [ENTITY_TYPES.COURSE]: {
    endpoint: '/share/course-access',
    paramName: 'course',
  },
};

/**
 * Get access list for an entity
 * @param {string} token - Authentication token
 * @param {string} entityType - Type of entity (from ENTITY_TYPES)
 * @param {number} entityId - ID of the entity
 * @returns {Promise<Array>} - List of access entries
 */
export const getAccessList = (token, entityType, entityId, options = {}) => {
  const { endpoint, paramName } = ENTITY_CONFIG[entityType];

  return apiHandler(
      () => api.get(`${endpoint}/`, {
        params: { [paramName]: entityId },
        headers: { Authorization: `Token ${token}` }
      }),
      {
        ...options,
        transform: data => data.results || data,
        defaultValue: []
      }
  );
};

/**
 * Add access for users or groups to an entity
 * @param {string} token - Authentication token
 * @param {string} entityType - Type of entity (from ENTITY_TYPES)
 * @param {number} entityId - ID of the entity
 * @param {Array<number>} userIds - List of user IDs to add
 * @param {Array<number>} groupIds - List of group IDs to add
 * @returns {Promise<boolean>} - Success status
 */
export const addAccess = (token, entityType, entityId, userIds = [], groupIds = [], options = {}) => {
  const { endpoint, paramName } = ENTITY_CONFIG[entityType];

  return apiHandler(
      async () => {
        const promises = [];

        // Add users
        for (const userId of userIds) {
          promises.push(
              api.post(`${endpoint}/`,
                  {
                    [paramName]: entityId,
                    user: userId,
                    group: null
                  },
                  {
                    headers: { Authorization: `Token ${token}` }
                  }
              )
          );
        }

        // Add groups
        for (const groupId of groupIds) {
          promises.push(
              api.post(`${endpoint}/`,
                  {
                    [paramName]: entityId,
                    group: groupId,
                    user: null
                  },
                  {
                    headers: { Authorization: `Token ${token}` }
                  }
              )
          );
        }

        await Promise.all(promises);
        return true;
      },
      {
        ...options,
        defaultValue: true
      }
  );
};

/**
 * Remove access for a specific access entry
 * @param {string} token - Authentication token
 * @param {string} entityType - Type of entity (from ENTITY_TYPES)
 * @param {number} accessId - ID of the access entry to remove
 * @returns {Promise<boolean>} - Success status
 */
export const removeAccess = (token, entityType, accessId, options = {}) => {
  const { endpoint } = ENTITY_CONFIG[entityType];

  return apiHandler(
      () => api.delete(`${endpoint}/${accessId}/`, {
        headers: { Authorization: `Token ${token}` }
      }),
      {
        ...options,
        defaultValue: true
      }
  );
};
